import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Sidebar } from './Sidebar';
import { HealthCheck } from './HealthCheck';
import { NotificationBell } from './NotificationBell';
import { DashboardView } from './views/DashboardView';
import { LeaderboardView } from './views/LeaderboardView';
import { DealHealthView } from './views/DealHealthView';
import { OperativeDashboardView } from './views/OperativeDashboardView';
import { AIHelperView } from './views/AIHelperView';
import { LeadsView } from './views/LeadsView';
import { TaxDelinquentListsView } from './views/TaxDelinquentListsView';
import { RegularListsView } from './views/RegularListsView';
import { ImportView } from './views/ImportView';
import { JVPortalView } from './views/JVPortalView';
import { AdminPortalView } from './views/AdminPortalView';
import { SuperAdminPortalView } from './views/SuperAdminPortalView';
import { TaxLatesDataAdminView } from './views/TaxLatesDataAdminView';
import { SettingsView } from './views/SettingsView';
import { ProfileView } from './views/ProfileView';
import { User } from 'lucide-react';
import { StudentProvider } from '../contexts/StudentContext';
import { ToastProvider } from '../contexts/ToastContext';

export function AuthenticatedApp() {
  const { user } = useAuthenticator((context) => [context.user]);
  // Default to dashboard while leaderboard is hidden from nav.
  const [currentView, setCurrentView] = useState('dashboard');
  const [viewParams, setViewParams] = useState<any>(null);
  const [userRole, setUserRole] = useState<string>('User');
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);

  // Handler to change view AND update hash
  const handleViewChange = (view: string, params?: any) => {
    setCurrentView(view);
    setViewParams(params || null);
    window.history.pushState({ view, params }, '', `#/${view}`);
  };

  // Load profile image on mount
  useEffect(() => {
    const loadProfileImage = async () => {
      try {
        const session = await fetchAuthSession();
        const response = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${session.tokens?.idToken?.toString()}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const profile = await response.json();
          if (profile.profile_image_url_presigned) {
            setProfileImageUrl(profile.profile_image_url_presigned);
          }
        }
      } catch (error) {
        console.error('Error loading profile image:', error);
      }
    };

    if (user) {
      loadProfileImage();
    }

    // Listen for profile image updates
    const handleProfileImageUpdate = (event: CustomEvent) => {
      if (event.detail) {
        setProfileImageUrl(event.detail);
      }
    };

    window.addEventListener('profile-image-updated', handleProfileImageUpdate as EventListener);
    return () => window.removeEventListener('profile-image-updated', handleProfileImageUpdate as EventListener);
  }, [user]);

  // Handle deep links from URL on mount (for email links) AND restore view from hash
  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;

    // Parse paths like /admin/tax-lists/{id}
    const taxListMatch = path.match(/\/admin\/tax-lists\/([a-zA-Z0-9_-]+)/i);
    if (taxListMatch) {
      const taxOrderId = taxListMatch[1];
      setCurrentView('taxlatesdata-admin');
      setViewParams({ taxOrderId, tab: 'orders' });
      // Clear the URL to base path
      window.history.replaceState({}, '', '/');
      return;
    }

    // Parse paths like /admin/dfpb-orders/{id}
    const dfpbOrderMatch = path.match(/\/admin\/dfpb-orders\/([a-f0-9-]+)/i);
    if (dfpbOrderMatch) {
      const orderId = dfpbOrderMatch[1];
      setCurrentView('admin-portal');
      setViewParams({ orderId, tab: 'dfpb-orders' });
      // Clear the URL to base path
      window.history.replaceState({}, '', '/');
      return;
    }

    // Parse paths like /admin/jv-submissions/{id}
    const jvSubmissionMatch = path.match(/\/admin\/jv-submissions\/([a-f0-9-]+)/i);
    if (jvSubmissionMatch) {
      const submissionId = jvSubmissionMatch[1];
      const scrollToComments = hash === '#comments';
      setCurrentView('admin-portal');
      setViewParams({ submissionId, tab: 'jv-submissions', scrollToComments });
      // Clear the URL to base path
      window.history.replaceState({}, '', '/');
      return;
    }

    // Parse paths like /jv-submissions/{id} (for regular users)
    const userJvSubmissionMatch = path.match(/^\/jv-submissions\/([a-f0-9-]+)/i);
    if (userJvSubmissionMatch) {
      const submissionId = userJvSubmissionMatch[1];
      const scrollToComments = hash === '#comments';
      setCurrentView('jv-portal');
      setViewParams({ submissionId, scrollToComments });
      // Clear the URL to base path (hash will be used for scroll behavior)
      window.history.replaceState({}, '', '/');
      return;
    }

    // Parse hash-based paths like #/admin-portal?submissionId=xxx OR restore view from hash on page load
    if (hash) {
      const hashPath = hash.substring(1); // Remove #
      const [view, queryString] = hashPath.split('?');

      // Restore view from hash (e.g., #/profile or #/jv-portal). Compared
      // against the default view so #/dashboard still restores correctly now
      // that the leaderboard is the landing page.
      const viewName = view.replace('/', '');
      if (viewName && viewName !== 'leaderboard') {
        setCurrentView(viewName);

        // Parse query params if present
        if (queryString) {
          const params = Object.fromEntries(new URLSearchParams(queryString));
          setViewParams(params);
        }
      }

      // DON'T clear hash - keep it so page refresh works
    }
  }, []);

  // Listen for navigation events from dashboard
  useEffect(() => {
    const handleNavigate = (event: CustomEvent) => {
      let newView: string;
      let newParams: any = null;

      if (typeof event.detail === 'object' && event.detail !== null) {
        // Navigation with parameters: { view: 'admin-portal', params: { submissionId: '123' } }
        newView = event.detail.view;
        newParams = event.detail.params || null;
      } else {
        // Simple navigation: just the view name string
        newView = event.detail;
      }

      // Update state
      setCurrentView(newView);
      setViewParams(newParams);

      // Push to browser history so back button works
      const historyState = { view: newView, params: newParams };
      window.history.pushState(historyState, '', `#/${newView}`);
    };

    window.addEventListener('navigate', handleNavigate as EventListener);
    return () => window.removeEventListener('navigate', handleNavigate as EventListener);
  }, []);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state && event.state.view) {
        setCurrentView(event.state.view);
        setViewParams(event.state.params || null);
      } else {
        // No state - check hash before defaulting to dashboard
        const hash = window.location.hash;
        if (hash) {
          const hashPath = hash.substring(1);
          const [view] = hashPath.split('?');
          const viewName = view.replace('/', '');
          if (viewName && viewName !== 'dashboard') {
            setCurrentView(viewName);
            return;
          }
        }
        // No state and no hash - go to the splash page
        setCurrentView('leaderboard');
        setViewParams(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Get user groups and role from Cognito
  const [userGroups, setUserGroups] = useState<string[]>([]);

  useEffect(() => {
    const loadUserRole = async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const groups = (idToken?.payload['cognito:groups'] as string[]) || [];

        setUserGroups(groups);

        // Role-based splash routing, strict 4-tier hierarchy:
        //   - super_admin → Operative Dashboard (admin monitoring)
        //   - admin_manager, admin → Admin Portal (Deals tab is default,
        //     which now carries the LLM-scored deal queue + pipeline hero)
        //   - students / partners → Leaderboard
        // Only fires when the user arrived at the root with no explicit view
        // in the URL — deep links (#/admin-portal, #/settings, etc.) are
        // honored regardless of role.
        const hasExplicitView =
          window.location.hash.startsWith('#/') || window.location.pathname !== '/';
        if (!hasExplicitView) {
          if (groups.includes('super_admin')) {
            setCurrentView('operative-dashboard');
          } else if (groups.includes('admin_manager') || groups.includes('admin')) {
            setCurrentView('admin-portal');
          }
        }

        // Determine role label
        let roleLabel = 'User';
        if (groups.includes('super_admin')) {
          roleLabel = 'Super Admin';
        } else if (groups.includes('admin_manager')) {
          roleLabel = 'Admin Manager';
        } else if (groups.includes('admin')) {
          roleLabel = 'Admin';
        } else {
          // For non-admin users, check tags from profile
          try {
            const profileResponse = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${idToken?.toString()}`,
                'Content-Type': 'application/json'
              }
            });
            if (profileResponse.ok) {
              const profile = await profileResponse.json();
              const tags = profile.tags || [];

              // Prioritize DFG over BPG
              if (tags.includes('DFPB')) {
                roleLabel = 'Debt Free Property Blueprint Member';
              } else if (tags.includes('BPG')) {
                roleLabel = 'Blueprint Group Member';
              }
            }
          } catch (err) {
            console.error('Error loading profile tags:', err);
          }
        }

        setUserRole(roleLabel);
      } catch (error) {
        console.error('Error loading user role:', error);
      }
    };

    if (user) {
      loadUserRole();
    }
  }, [user]);

  // Check if user has admin access
  const hasAdminAccess = () => {
    return userGroups.includes('super_admin') ||
           userGroups.includes('admin_manager') ||
           userGroups.includes('admin');
  };

  const isSuperAdmin = () => {
    return userGroups.includes('super_admin');
  };

  const isTaxLatesDataAdmin = () => {
    return userGroups.includes('taxlatesdata');
  };

  // Page title shown in the top bar. Must reflect the ACTUAL rendered view —
  // if RBAC blocks a route, the title must not reveal the gated view name.
  const getPageTitle = () => {
    if ((currentView === 'operative-dashboard' || currentView === 'super-admin-portal') && !isSuperAdmin()) {
      return 'Leaderboard';
    }
    if ((currentView === 'admin-portal' || currentView === 'deal-health') && !hasAdminAccess()) {
      return 'Leaderboard';
    }
    if (currentView === 'properties') {
      return hasAdminAccess() ? 'Properties' : 'Leaderboard';
    }
    return currentView.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const renderView = () => {
    switch (currentView) {
      case 'leaderboard':
        return <LeaderboardView />;
      case 'deal-health':
        // Historical route — the deal-scoring queue lived here briefly
        // as a standalone view before being merged into Admin Portal's
        // Deals tab. Kept so old bookmarks land in the right place.
        if (!hasAdminAccess()) {
          return <LeaderboardView />;
        }
        return <AdminPortalView initialParams={{ tab: 'jv-submissions' }} />;
      case 'operative-dashboard':
        // Super-admin ONLY. Anyone else falls back to Leaderboard rather
        // than leaking the shape of the page they can't see.
        if (!isSuperAdmin()) {
          return <LeaderboardView />;
        }
        return <OperativeDashboardView />;
      case 'dashboard':
        return <DashboardView isAdmin={hasAdminAccess() && !isSuperAdmin()} />;
      case 'ai-helper':
        return <AIHelperView />;
      case 'leads':
        return <LeadsView />;
      case 'tax-delinquent-lists':
        if (hasAdminAccess() && !isSuperAdmin()) return <DashboardView isAdmin={true} />;
        return <TaxDelinquentListsView initialParams={viewParams} />;
      case 'regular-lists':
        return <RegularListsView />;
      case 'import':
        return <ImportView />;
      case 'jv-portal':
        return <JVPortalView initialParams={viewParams} />;
      case 'super-admin-portal':
        // Require super_admin group
        if (!isSuperAdmin()) {
          return (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600 mb-4">Access Denied</p>
                <p className="text-gray-600">You don't have permission to access Super Admin Portal.</p>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'dashboard' }))}
                  className="mt-4 px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-tdb-secondary"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          );
        }
        return <SuperAdminPortalView />;
      case 'admin-portal':
        // Require admin access (admin, admin_manager, or super_admin)
        if (!hasAdminAccess()) {
          return (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600 mb-4">Access Denied</p>
                <p className="text-gray-600">You don't have permission to access Admin Portal.</p>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'dashboard' }))}
                  className="mt-4 px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-tdb-secondary"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          );
        }
        return (
          <StudentProvider>
            <AdminPortalView initialParams={viewParams} />
          </StudentProvider>
        );
      case 'taxlatesdata-admin':
        // Require taxlatesdata group
        if (!isTaxLatesDataAdmin()) {
          return (
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600 mb-4">Access Denied</p>
                <p className="text-gray-600">You don't have permission to access TaxLatesData Admin.</p>
                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'dashboard' }))}
                  className="mt-4 px-6 py-2 bg-tdb-accent text-white rounded-lg hover:bg-tdb-secondary"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          );
        }
        return <TaxLatesDataAdminView initialParams={viewParams} />;
      case 'settings':
        return <SettingsView initialParams={viewParams} />;
      case 'profile':
        return <ProfileView />;
      default:
        return <LeaderboardView />;
    }
  };

  return (
    <ToastProvider>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <Sidebar currentView={currentView} onViewChange={handleViewChange} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header Bar */}
        <div className="bg-white shadow-sm border-b border-gray-200 px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-tdb-primary">
                {getPageTitle()}
              </h2>
            </div>
            <div className="flex items-center space-x-4">
              {/* Health Check Indicator */}
              <HealthCheck />

              {/* Notification Bell */}
              <NotificationBell onOpenSettings={() => {
                // Navigate to settings with notifications tab
                setCurrentView('settings');
                setViewParams({ tab: 'notifications' });
                window.history.pushState({ view: 'settings', params: { tab: 'notifications' } }, '', '#/settings');
              }} />

              {/* User Info */}
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">
                  {user?.signInDetails?.loginId || 'User'}
                </div>
                <div className="text-xs text-gray-500">{userRole}</div>
              </div>
              <button
                onClick={() => {
                  setCurrentView('settings');
                  setViewParams({ tab: 'profile' });
                  window.history.pushState({ view: 'settings', params: { tab: 'profile' } }, '', '#/settings');
                }}
                className="w-10 h-10 bg-tdb-accent rounded-full flex items-center justify-center text-white font-bold hover:bg-[#0ea5ef] transition-all transform hover:scale-110 cursor-pointer overflow-hidden"
              >
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span>{(user?.signInDetails?.loginId || 'U').charAt(0).toUpperCase()}</span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-8">
          {renderView()}
        </div>
      </div>
      </div>
    </ToastProvider>
  );
}
